/*
 * The admin API surface, as one object bound to a token provider.
 *
 * Binding the provider once, at construction, means no component has to remember
 * to pass a token - and there is no admin call in the codebase that can be written
 * without one.
 *
 * The shape of this surface follows the row / revision split and nothing else.
 * An article is one row with one slug and one body of text, so there is one path
 * per article and revisions hang directly off it:
 *
 *     /admin/articles/{id}
 *     /admin/articles/{id}/revisions[/{n}[/promote|/restore]]
 *
 * A release note (S42, 2026-10-12) is the same split a second time - a row in
 * kb.ReleaseNote carrying the address (product + version) and the publishing
 * gates, append-only revisions in kb.ReleaseNoteRevision carrying the title and
 * the text - so it gets the same paths under its own prefix, kebab-case like the
 * rest of the API, and methods named after the article ones they mirror:
 *
 *     /admin/release-notes/{id}
 *     /admin/release-notes/{id}/revisions[/{n}[/promote|/restore]]
 *     /admin/release-notes/{id}/images[/{name}]
 *
 * Parallel methods rather than one entity-generic set with a `kind` argument: a
 * release note and an article answer with different shapes (a version where an
 * article has a slug, no categories), and a call site that could pass either id
 * to either kind is the confusion this file exists to make impossible. What IS
 * shared is everything below the method names - the request helper, the image
 * transport, the error shape.
 *
 * The two save routes are the distinction worth keeping: appending a revision
 * (POST .../revisions with publish: false) parks a draft behind the published
 * text, which the filtered unique index on kb.ArticleRevision.IsCurrent is what
 * makes possible. Collapsing that into one "save" would remove the only thing the
 * revision table exists to express.
 *
 * Images hang off the article for the same reason revisions do - kb.ArticleImage is
 * keyed (ArticleId, FileName) - so they are /admin/articles/{id}/images[/{name}],
 * and a release note's off the release note (kb.ReleaseNoteImage, keyed
 * (ReleaseNoteId, FileName)). Three of the four calls do not go through
 * `request`, and each has a reason:
 *
 *   upload    XMLHttpRequest, not fetch, because fetch cannot report upload
 *             progress. A silent minute on a 4 MB paste is the failure this whole
 *             feature is most likely to produce, so the progress event is not a
 *             nicety.
 *   preview   raw fetch returning a Blob. The bytes are not JSON, and an <img>
 *             cannot send an Authorization header - see articleImageObjectUrl.
 *   delete    goes through `request`, but sends its flag twice; the comment on it
 *             says why.
 *
 * The error shape is the project's uniform ApiErrorBody in all three cases, so the
 * failures render in the same banners as everything else.
 */

import type {
  PromotedArticleEntry,
  AdminAttachment,
  AdminAttachmentDetail,
  AdminAttachmentListResponse,
  AdminAttachmentUploadResponse,
  AdminArticleDetail,
  AdminArticleImageBulkDeleteResponse,
  AdminArticleImageDeleteResponse,
  AdminArticleImageListResponse,
  AdminArticleImageUploadResponse,
  AdminArticleListResponse,
  AdminArticleReferencesResponse,
  AdminArticleStatusFilter,
  AdminArticleUpdateResponse,
  AdminIdentity,
  AdminOverview,
  AdminReleaseNoteDetail,
  AdminReleaseNoteListResponse,
  AdminReleaseNoteRevision,
  AdminReleaseNoteRevisionSaveResponse,
  AdminReleaseNoteRevisionSummary,
  AdminReleaseNoteSort,
  AdminReleaseNoteStatusFilter,
  AdminRevision,
  AdminRevisionSummary,
  AdminSiteSettings,
  ArticlesSettingsUpdateInput,
  ApiErrorBody,
  ArticleAudienceOption,
  ArticleCreateInput,
  ArticleLinkResolution,
  ArticleUpdateInput,
  AttachmentDownloadLink,
  AttachmentLinkResolution,
  Category,
  CategoryInput,
  DeleteImpact,
  DeleteImpactRequest,
  DeleteResult,
  DeleteTargetKind,
  Product,
  ProductInput,
  PublishActionInput,
  ReleaseNoteCreateInput,
  ReleaseNoteRevisionCreateInput,
  ReleaseNotesSettingsUpdateInput,
  ReleaseNoteUpdateInput,
  RevisionCreateInput,
  SiteSettingsUpdateInput,
  SiteTheme,
} from '@accotool-kb/shared';
import {
  articleImageUploadQuery,
  attachmentUploadQuery,
  declaredArticleImageUploadType,
} from '@accotool-kb/shared';
import { ApiError, request, type TokenProvider } from './client.js';

export interface AdminArticleFilterInput {
  q: string;
  products: number[];
  categories: number[];
  /** Typed from the shared list, so a filter the server cannot parse will not compile. */
  status: AdminArticleStatusFilter;
  page: number;
  pageSize: number;
  sort: 'modified' | 'created' | 'publish' | 'title';
}

/**
 * The release-note list's filter, as the admin list page holds it. The article
 * filter less the categories - a release note has none - with the status and sort
 * typed from the shared unions, so a view the server cannot parse will not compile.
 */
export interface AdminReleaseNoteFilterInput {
  q: string;
  products: number[];
  status: AdminReleaseNoteStatusFilter;
  page: number;
  pageSize: number;
  sort: AdminReleaseNoteSort;
}

/**
 * The knobs on an upload.
 *
 * `fileName` is omitted for a pasted image, which genuinely has none: the server
 * then generates `pasted-<n>.<ext>`, and the name it chose comes back in the
 * response. Insert THAT name into the markdown, never a name guessed here - the
 * server is the only party that can see what is already attached.
 */
export interface ArticleImageUploadOptions {
  /** The name to store it under. A name the CHECK constraint refuses is rejected. */
  fileName?: string;
  /** Overwrite an attachment of the same name instead of being refused. */
  replace?: boolean;
  /** Fraction uploaded, 0 to 1. Fired from the XMLHttpRequest progress event. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * The knobs on an attachment upload (S47). Unlike an image there is always a
 * name - it is what the reader downloads - and never a `replace` flag: replacing
 * a library file is its own call, because it replaces the file for every article
 * that uses it, and that should never be a side effect of picking a name.
 */
export interface AttachmentUploadOptions {
  /** As the author's file is called, normalised (normaliseAttachmentFileName). */
  fileName: string;
  /** Fraction uploaded, 0 to 1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * What the editor preview resolves (S47 added the second list): the [[kb:...]]
 * tokens against the articles, and the [[attachment:...]] tokens against the
 * whole library - an administrator's preview links every file there is.
 */
export interface PreviewLinks {
  linkTargets: ArticleLinkResolution[];
  attachmentLinks: AttachmentLinkResolution[];
}

/** The two catalogues, fetched together because every editor form needs both. */
export interface MetadataBundle {
  products: Product[];
  categories: Category[];
  /**
   * The audiences a private article can be for (S46): configuration on the
   * server, keys and names, in name order.
   */
  audiences: ArticleAudienceOption[];
}

/**
 * One address already in use: a slug, and the product it is taken under.
 *
 * A PAIR rather than a flat slug, because kb.Article.Slug is unique only within
 * a product (UQ_Article_ProductId_Slug). A flat list would be wrong in both
 * directions: it would mark a slug as taken that is free under the product the
 * author has chosen, and it would make the new-article page suffix an address
 * nothing was occupying. Both callers therefore filter by productId before they
 * look a slug up, which is the only reason this shape exists.
 */
export interface ArticleAddressInUse {
  productId: number;
  slug: string;
}

export interface AdminApi {
  me(): Promise<AdminIdentity>;
  overview(): Promise<AdminOverview>;

  listArticles(
    filter: AdminArticleFilterInput,
    signal?: AbortSignal,
  ): Promise<AdminArticleListResponse>;
  getArticle(articleId: string): Promise<AdminArticleDetail>;
  /**
   * The articles - and, since S42, the release notes - whose text links to this
   * one, as two lists: `items` (other articles) and `releaseNotes`. Its own call,
   * deliberately: the server finds them by scanning every other article's and
   * every release note's current and latest revision, which grows with the
   * knowledge base, so the editor asks for it BESIDE the article rather than
   * inside it and opens without waiting.
   */
  listArticleReferences(articleId: string): Promise<AdminArticleReferencesResponse>;
  createArticle(input: ArticleCreateInput): Promise<AdminArticleDetail>;
  /**
   * Saves the properties. The response carries the article and - when the slug
   * changed - what that did to the other articles linking here by the old
   * address, so the editor can report it rather than leave the author to look.
   */
  updateArticle(articleId: string, input: ArticleUpdateInput): Promise<AdminArticleUpdateResponse>;
  publishAction(articleId: string, action: PublishActionInput): Promise<AdminArticleDetail>;
  /**
   * Resolves the [[kb:...]] and [[attachment:...]] tokens in unsaved text, for
   * the preview in the editor.
   */
  previewLinks(content: string): Promise<PreviewLinks>;
  /**
   * Every address in use, as (productId, slug) pairs - so a collision can be
   * caught, or suffixed away, before the insert rather than after
   * UQ_Article_ProductId_Slug has refused it.
   *
   * The name is historical and the route is still /articles/slugs; what changed
   * is that a slug on its own no longer identifies an address. See
   * ArticleAddressInUse for why the product half travels with it.
   */
  articleSlugs(): Promise<ArticleAddressInUse[]>;

  /** The promoted shortlist, in order, including entries the public cannot see. */
  listPromoted(): Promise<PromotedArticleEntry[]>;
  /** Replaces the shortlist; array order is display order. Returns the stored list. */
  setPromoted(articleIds: string[]): Promise<PromotedArticleEntry[]>;

  listRevisions(articleId: string): Promise<AdminRevisionSummary[]>;
  getRevision(articleId: string, revisionNumber: number): Promise<AdminRevision>;
  saveRevision(
    articleId: string,
    input: RevisionCreateInput,
  ): Promise<{ revision: AdminRevision; article: AdminArticleDetail }>;
  promoteRevision(articleId: string, revisionNumber: number): Promise<AdminArticleDetail>;
  restoreRevision(
    articleId: string,
    revisionNumber: number,
    publish: boolean,
  ): Promise<{ revision: AdminRevision; article: AdminArticleDetail }>;

  /** Metadata only. The bytes are never in a list response - see ArticleImage. */
  listArticleImages(articleId: string): Promise<AdminArticleImageListResponse>;
  uploadArticleImage(
    articleId: string,
    file: Blob,
    options?: ArticleImageUploadOptions,
  ): Promise<AdminArticleImageUploadResponse>;
  deleteArticleImage(
    articleId: string,
    fileName: string,
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageDeleteResponse>;
  /**
   * Detaches several images in one request - all or nothing, under the same
   * saved-text protection as the single delete. Answers by name, so the panel
   * removes exactly what went and not what it asked for.
   */
  deleteArticleImages(
    articleId: string,
    fileNames: string[],
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageBulkDeleteResponse>;
  /**
   * The stored bytes as a blob: URL, for the editor preview.
   *
   * The whole /api/admin router is behind requireAdmin, so the image endpoint needs
   * a bearer token - and an <img> element cannot send a header. Fetching the bytes
   * with the token and handing the element an object URL is the only way the
   * preview can show an image that no reader may see yet.
   *
   * The caller owns the returned URL and must revoke it, or the bytes stay in
   * memory for the life of the document.
   */
  articleImageObjectUrl(articleId: string, fileName: string, signal?: AbortSignal): Promise<string>;
  /**
   * The endpoint path for one image, as a string.
   *
   * Used as the src of last resort: a name the preview could not resolve gets this,
   * the request answers 401 or 404, and Markdown turns the failure into a visible
   * placeholder. That is deliberately better than an <img> with no src, which is an
   * invisible gap the author cannot see they have created.
   */
  articleImagePath(articleId: string, fileName: string): string;

  /*
   * Release notes (S42, 2026-10-12): the article calls above, one for one, under
   * /admin/release-notes. Same concurrency story - the row (product, version,
   * publishing gates) under its rowVersion, the text as append-only revisions
   * under baseRevisionNumber - and the same image transport; the comments on the
   * article methods apply to these unchanged.
   */
  listReleaseNotes(
    filter: AdminReleaseNoteFilterInput,
    signal?: AbortSignal,
  ): Promise<AdminReleaseNoteListResponse>;
  getReleaseNote(releaseNoteId: string): Promise<AdminReleaseNoteDetail>;
  /** The row and its first revision in one request, as createArticle does. */
  createReleaseNote(input: ReleaseNoteCreateInput): Promise<AdminReleaseNoteDetail>;
  /**
   * Saves the properties - the product and the version, which are the note's
   * address - under the row's rowVersion. A (product, version) pair another note
   * already has is a 409 with the message under `version`.
   */
  updateReleaseNote(
    releaseNoteId: string,
    input: ReleaseNoteUpdateInput,
  ): Promise<AdminReleaseNoteDetail>;
  releaseNotePublishAction(
    releaseNoteId: string,
    action: PublishActionInput,
  ): Promise<AdminReleaseNoteDetail>;
  /** Clears the current-revision pointer: the text is withdrawn, the history kept. */
  releaseNoteUnpublishText(releaseNoteId: string): Promise<AdminReleaseNoteDetail>;
  listReleaseNoteRevisions(releaseNoteId: string): Promise<AdminReleaseNoteRevisionSummary[]>;
  getReleaseNoteRevision(
    releaseNoteId: string,
    revisionNumber: number,
  ): Promise<AdminReleaseNoteRevision>;
  saveReleaseNoteRevision(
    releaseNoteId: string,
    input: ReleaseNoteRevisionCreateInput,
  ): Promise<AdminReleaseNoteRevisionSaveResponse>;
  promoteReleaseNoteRevision(
    releaseNoteId: string,
    revisionNumber: number,
  ): Promise<AdminReleaseNoteDetail>;
  restoreReleaseNoteRevision(
    releaseNoteId: string,
    revisionNumber: number,
    publish: boolean,
  ): Promise<AdminReleaseNoteRevisionSaveResponse>;
  listReleaseNoteImages(releaseNoteId: string): Promise<AdminArticleImageListResponse>;
  uploadReleaseNoteImage(
    releaseNoteId: string,
    file: Blob,
    options?: ArticleImageUploadOptions,
  ): Promise<AdminArticleImageUploadResponse>;
  deleteReleaseNoteImage(
    releaseNoteId: string,
    fileName: string,
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageDeleteResponse>;
  deleteReleaseNoteImages(
    releaseNoteId: string,
    fileNames: string[],
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageBulkDeleteResponse>;
  /** As articleImageObjectUrl: a blob: URL the caller owns and must revoke. */
  releaseNoteImageObjectUrl(
    releaseNoteId: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string>;
  /** As articleImagePath: the src of last resort, which fails visibly. */
  releaseNoteImagePath(releaseNoteId: string, fileName: string): string;

  /*
   * Attachments (2026-10-28, S47): a library of files in the deployment's
   * storage account, which an article LISTS (at most ten) and may LINK from its
   * text. The bytes go up through the same XHR as an image, always as
   * application/octet-stream - the server takes the type from the name and the
   * bytes, and refuses a body sent as JSON or as a form - and come DOWN from the
   * storage account itself, through a short-lived link the server signs for one
   * file: attachmentDownloadLink, then the browser goes there.
   */
  listAttachments(): Promise<AdminAttachment[]>;
  /** One file and every article that uses it - listed, or linked in its text. */
  getAttachment(attachmentId: string): Promise<AdminAttachmentDetail>;
  /** Into the library, on no list. */
  uploadAttachment(file: Blob, options: AttachmentUploadOptions): Promise<AdminAttachmentUploadResponse>;
  /** New bytes - and the name they came with - for every article that uses the file. */
  replaceAttachmentFile(
    attachmentId: string,
    file: Blob,
    options: AttachmentUploadOptions,
  ): Promise<AdminAttachmentUploadResponse>;
  /**
   * Out of the library and off every list. Refused with a 409 while an article
   * uses the file, unless `acknowledgeUsage` says the administrator was shown which.
   */
  deleteAttachment(attachmentId: string, options?: { acknowledgeUsage?: boolean }): Promise<void>;
  /** A link that downloads the file for the next few minutes. */
  attachmentDownloadLink(attachmentId: string): Promise<AttachmentDownloadLink>;
  /** Into the library AND onto this article's list, in one request. */
  uploadArticleAttachment(
    articleId: string,
    file: Blob,
    options: AttachmentUploadOptions,
  ): Promise<AdminAttachmentUploadResponse>;
  /** A file the library already has, onto this article's list. */
  listAttachmentOnArticle(
    articleId: string,
    attachmentId: string,
  ): Promise<{ attachment: AdminAttachment; outcome: 'listed' | 'already-listed' }>;
  /** Off this article's list. The library keeps the file. */
  unlistAttachmentFromArticle(articleId: string, attachmentId: string): Promise<void>;

  metadata(): Promise<MetadataBundle>;
  createProduct(input: ProductInput): Promise<Product>;
  updateProduct(productId: number, input: ProductInput): Promise<Product>;
  createCategory(input: CategoryInput): Promise<Category>;
  updateCategory(categoryId: number, input: CategoryInput): Promise<Category>;

  deleteImpact(target: DeleteImpactRequest): Promise<DeleteImpact>;
  performDelete(
    kind: DeleteTargetKind,
    targetId: string,
    confirmationToken: string,
    options?: { choices?: Record<string, string | null> },
  ): Promise<DeleteResult>;

  /*
   * Site settings: one row, seven calls. Every write returns the whole row with
   * its new rowversion, so the Settings page holds one object and never reloads
   * after its own change. The icon goes up as raw bytes like an article image -
   * same wire format, same gate, a 1 MB cap - and down through
   * /api/public/site/icon like any reader gets it, so there is no admin preview
   * call: the stored icon is public by definition.
   */
  getSiteSettings(): Promise<AdminSiteSettings>;
  updateSiteSettings(input: SiteSettingsUpdateInput): Promise<AdminSiteSettings>;
  /** The articles page's title and subtitle (2026-10-15), gated on the same rowversion. */
  updateArticlesSettings(input: ArticlesSettingsUpdateInput): Promise<AdminSiteSettings>;
  /** The release-note list's title and subtitle (2026-10-15), gated on the same rowversion. */
  updateReleaseNotesSettings(input: ReleaseNotesSettingsUpdateInput): Promise<AdminSiteSettings>;
  uploadSiteIcon(file: Blob, options?: { signal?: AbortSignal }): Promise<AdminSiteSettings>;
  deleteSiteIcon(): Promise<AdminSiteSettings>;
  /** Applies a theme for every reader. Previewing is the browser's business; this is the save. */
  updateSiteTheme(theme: SiteTheme): Promise<AdminSiteSettings>;
}

export function createAdminApi(token: TokenProvider): AdminApi {
  const call = <T>(
    path: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: unknown;
      query?: Record<string, string | number | boolean | Array<string | number> | null | undefined>;
      signal?: AbortSignal;
    } = {},
  ): Promise<T> => request<T>(`/admin${path}`, { ...options, token });

  const articlePath = (articleId: string) => `/articles/${encodeURIComponent(articleId)}`;
  const revisionsPath = (articleId: string) => `${articlePath(articleId)}/revisions`;
  const releaseNotePath = (releaseNoteId: string) =>
    `${RELEASE_NOTES_API}/${encodeURIComponent(releaseNoteId)}`;
  const releaseNoteRevisionsPath = (releaseNoteId: string) =>
    `${releaseNotePath(releaseNoteId)}/revisions`;

  return {
    me: () => call<AdminIdentity>('/me'),
    overview: () => call<AdminOverview>('/overview'),

    listArticles: (filter, signal) =>
      call<AdminArticleListResponse>('/articles', {
        query: {
          q: filter.q,
          products: filter.products,
          categories: filter.categories,
          status: filter.status,
          page: filter.page,
          pageSize: filter.pageSize,
          sort: filter.sort,
        },
        ...(signal ? { signal } : {}),
      }),

    listPromoted: () =>
      call<{ items: PromotedArticleEntry[] }>('/promoted').then((body) => body.items),

    setPromoted: (articleIds) =>
      call<{ items: PromotedArticleEntry[] }>('/promoted', {
        method: 'PUT',
        body: { articleIds },
      }).then((body) => body.items),

    getArticle: (articleId) => call<AdminArticleDetail>(articlePath(articleId)),

    listArticleReferences: (articleId) =>
      call<AdminArticleReferencesResponse>(`${articlePath(articleId)}/references`).then(
        // Both lists always present for the caller. A server from before S42
        // answers `items` alone, and an absent list must read as "none" here
        // rather than as a crash in the panel that maps over it.
        (result) => ({ items: result.items, releaseNotes: result.releaseNotes ?? [] }),
      ),

    createArticle: (input) =>
      call<AdminArticleDetail>('/articles', { method: 'POST', body: input }),

    updateArticle: (articleId, input) =>
      call<AdminArticleUpdateResponse>(articlePath(articleId), { method: 'PUT', body: input }),

    publishAction: (articleId, action) =>
      call<AdminArticleDetail>(`${articlePath(articleId)}/publish`, {
        method: 'POST',
        body: action,
      }),

    previewLinks: (content) =>
      call<{ linkTargets: ArticleLinkResolution[]; attachmentLinks?: AttachmentLinkResolution[] }>(
        '/articles/preview-links',
        { method: 'POST', body: { content } },
      ).then((result) => ({
        linkTargets: result.linkTargets,
        // A server from before S47 sends no second list; none is what it means.
        attachmentLinks: result.attachmentLinks ?? [],
      })),

    articleSlugs: () =>
      call<{ slugs: ArticleAddressInUse[] }>('/articles/slugs').then((result) => result.slugs),

    listRevisions: (articleId) =>
      call<{ items: AdminRevisionSummary[] }>(revisionsPath(articleId)).then(
        (result) => result.items,
      ),

    getRevision: (articleId, revisionNumber) =>
      call<AdminRevision>(`${revisionsPath(articleId)}/${revisionNumber}`),

    saveRevision: (articleId, input) =>
      call<{ revision: AdminRevision; article: AdminArticleDetail }>(revisionsPath(articleId), {
        method: 'POST',
        body: input,
      }),

    promoteRevision: (articleId, revisionNumber) =>
      call<AdminArticleDetail>(`${revisionsPath(articleId)}/${revisionNumber}/promote`, {
        method: 'POST',
        body: {},
      }),

    restoreRevision: (articleId, revisionNumber, publish) =>
      call<{ revision: AdminRevision; article: AdminArticleDetail }>(
        `${revisionsPath(articleId)}/${revisionNumber}/restore`,
        { method: 'POST', body: { publish } },
      ),

    listArticleImages: (articleId) =>
      call<AdminArticleImageListResponse>(`${articlePath(articleId)}/images`),

    uploadArticleImage: (articleId, file, options = {}) =>
      uploadImage(token, `/api/admin${articlePath(articleId)}/images`, file, options),

    deleteArticleImage: (articleId, fileName, options = {}) =>
      call<AdminArticleImageDeleteResponse>(
        `${articlePath(articleId)}/images/${encodeURIComponent(fileName)}`,
        {
          method: 'DELETE',
          // The acknowledgement travels in the query string AND the body. It is one
          // flag either way, and articleImageDeleteSchema coerces a string form of
          // it precisely so that a DELETE - which has no natural body in every HTTP
          // stack - can carry it as a parameter. Sending both costs a dozen bytes
          // and removes a way for the two ends to disagree about where it lives.
          query: { acknowledgeReferences: options.acknowledgeReferences === true },
          body: { acknowledgeReferences: options.acknowledgeReferences === true },
        },
      ),

    deleteArticleImages: (articleId, fileNames, options = {}) =>
      call<AdminArticleImageBulkDeleteResponse>(`${articlePath(articleId)}/images/delete`, {
        method: 'POST',
        body: { fileNames, acknowledgeReferences: options.acknowledgeReferences === true },
      }),

    articleImageObjectUrl: async (articleId, fileName, signal) => {
      const blob = await fetchImageBlob(token, adminArticleImagePath(articleId, fileName), signal);
      return URL.createObjectURL(blob);
    },

    articleImagePath: (articleId, fileName) => adminArticleImagePath(articleId, fileName),

    listReleaseNotes: (filter, signal) =>
      call<AdminReleaseNoteListResponse>(RELEASE_NOTES_API, {
        query: {
          q: filter.q,
          products: filter.products,
          status: filter.status,
          page: filter.page,
          pageSize: filter.pageSize,
          sort: filter.sort,
        },
        ...(signal ? { signal } : {}),
      }),

    getReleaseNote: (releaseNoteId) =>
      call<AdminReleaseNoteDetail>(releaseNotePath(releaseNoteId)),

    createReleaseNote: (input) =>
      call<AdminReleaseNoteDetail>(RELEASE_NOTES_API, { method: 'POST', body: input }),

    updateReleaseNote: (releaseNoteId, input) =>
      call<AdminReleaseNoteDetail>(releaseNotePath(releaseNoteId), { method: 'PUT', body: input }),

    releaseNotePublishAction: (releaseNoteId, action) =>
      call<AdminReleaseNoteDetail>(`${releaseNotePath(releaseNoteId)}/publish`, {
        method: 'POST',
        body: action,
      }),

    releaseNoteUnpublishText: (releaseNoteId) =>
      call<AdminReleaseNoteDetail>(`${releaseNotePath(releaseNoteId)}/unpublish-text`, {
        method: 'POST',
        body: {},
      }),

    listReleaseNoteRevisions: (releaseNoteId) =>
      call<{ items: AdminReleaseNoteRevisionSummary[] }>(
        releaseNoteRevisionsPath(releaseNoteId),
      ).then((result) => result.items),

    getReleaseNoteRevision: (releaseNoteId, revisionNumber) =>
      call<AdminReleaseNoteRevision>(
        `${releaseNoteRevisionsPath(releaseNoteId)}/${revisionNumber}`,
      ),

    saveReleaseNoteRevision: (releaseNoteId, input) =>
      call<AdminReleaseNoteRevisionSaveResponse>(releaseNoteRevisionsPath(releaseNoteId), {
        method: 'POST',
        body: input,
      }),

    promoteReleaseNoteRevision: (releaseNoteId, revisionNumber) =>
      call<AdminReleaseNoteDetail>(
        `${releaseNoteRevisionsPath(releaseNoteId)}/${revisionNumber}/promote`,
        { method: 'POST', body: {} },
      ),

    restoreReleaseNoteRevision: (releaseNoteId, revisionNumber, publish) =>
      call<AdminReleaseNoteRevisionSaveResponse>(
        `${releaseNoteRevisionsPath(releaseNoteId)}/${revisionNumber}/restore`,
        { method: 'POST', body: { publish } },
      ),

    listReleaseNoteImages: (releaseNoteId) =>
      call<AdminArticleImageListResponse>(`${releaseNotePath(releaseNoteId)}/images`),

    uploadReleaseNoteImage: (releaseNoteId, file, options = {}) =>
      uploadImage(token, `/api/admin${releaseNotePath(releaseNoteId)}/images`, file, options),

    deleteReleaseNoteImage: (releaseNoteId, fileName, options = {}) =>
      call<AdminArticleImageDeleteResponse>(
        `${releaseNotePath(releaseNoteId)}/images/${encodeURIComponent(fileName)}`,
        {
          method: 'DELETE',
          // In the query string AND the body, for the reason given on
          // deleteArticleImage: the server reads the same schema for both.
          query: { acknowledgeReferences: options.acknowledgeReferences === true },
          body: { acknowledgeReferences: options.acknowledgeReferences === true },
        },
      ),

    deleteReleaseNoteImages: (releaseNoteId, fileNames, options = {}) =>
      call<AdminArticleImageBulkDeleteResponse>(
        `${releaseNotePath(releaseNoteId)}/images/delete`,
        {
          method: 'POST',
          body: { fileNames, acknowledgeReferences: options.acknowledgeReferences === true },
        },
      ),

    releaseNoteImageObjectUrl: async (releaseNoteId, fileName, signal) => {
      const blob = await fetchImageBlob(
        token,
        adminReleaseNoteImagePath(releaseNoteId, fileName),
        signal,
      );
      return URL.createObjectURL(blob);
    },

    releaseNoteImagePath: (releaseNoteId, fileName) =>
      adminReleaseNoteImagePath(releaseNoteId, fileName),

    listAttachments: () =>
      call<AdminAttachmentListResponse>(ATTACHMENTS_API).then((result) => result.items),

    getAttachment: (attachmentId) => call<AdminAttachmentDetail>(attachmentPath(attachmentId)),

    uploadAttachment: (file, options) =>
      uploadAttachmentBytes(token, 'POST', `/api/admin${ATTACHMENTS_API}`, file, options),

    replaceAttachmentFile: (attachmentId, file, options) =>
      uploadAttachmentBytes(token, 'PUT', `/api/admin${attachmentPath(attachmentId)}/file`, file, options),

    deleteAttachment: (attachmentId, options = {}) =>
      call<unknown>(attachmentPath(attachmentId), {
        method: 'DELETE',
        // The query string only: attachmentDeleteSchema reads it there, and a
        // DELETE with a body is the thing not every proxy forwards.
        query: { acknowledgeUsage: options.acknowledgeUsage === true },
      }).then(() => undefined),

    attachmentDownloadLink: (attachmentId) =>
      call<AttachmentDownloadLink>(`${attachmentPath(attachmentId)}/link`),

    uploadArticleAttachment: (articleId, file, options) =>
      uploadAttachmentBytes(token, 'POST', `/api/admin${articlePath(articleId)}/attachments`, file, options),

    listAttachmentOnArticle: (articleId, attachmentId) =>
      call<{ attachment: AdminAttachment; outcome: 'listed' | 'already-listed' }>(
        `${articlePath(articleId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'PUT', body: {} },
      ),

    unlistAttachmentFromArticle: (articleId, attachmentId) =>
      call<unknown>(`${articlePath(articleId)}/attachments/${encodeURIComponent(attachmentId)}`, {
        method: 'DELETE',
      }).then(() => undefined),

    metadata: () => call<MetadataBundle>('/metadata'),

    createProduct: (input) => call<Product>('/products', { method: 'POST', body: input }),
    updateProduct: (productId, input) =>
      call<Product>(`/products/${productId}`, { method: 'PUT', body: input }),

    createCategory: (input) => call<Category>('/categories', { method: 'POST', body: input }),
    updateCategory: (categoryId, input) =>
      call<Category>(`/categories/${categoryId}`, { method: 'PUT', body: input }),

    deleteImpact: (target) =>
      call<DeleteImpact>('/delete-impact', { method: 'POST', body: target }),

    performDelete: (kind, targetId, confirmationToken, options = {}) =>
      call<DeleteResult>('/delete', {
        method: 'POST',
        body: {
          kind,
          targetId,
          confirmationToken,
          ...(options.choices ? { choices: options.choices } : {}),
        },
      }),

    getSiteSettings: () => call<AdminSiteSettings>('/settings'),

    updateSiteSettings: (input) =>
      call<AdminSiteSettings>('/settings', { method: 'PUT', body: input }),

    updateArticlesSettings: (input) =>
      call<AdminSiteSettings>('/settings/articles', { method: 'PUT', body: input }),

    updateReleaseNotesSettings: (input) =>
      call<AdminSiteSettings>('/settings/release-notes', { method: 'PUT', body: input }),

    uploadSiteIcon: (file, options = {}) =>
      postImageBytes<AdminSiteSettings>(token, '/api/admin/settings/icon', file, options),

    deleteSiteIcon: () => call<AdminSiteSettings>('/settings/icon', { method: 'DELETE' }),

    updateSiteTheme: (theme) =>
      call<AdminSiteSettings>('/settings/theme', { method: 'PUT', body: { theme } }),
  };
}

// ---------------------------------------------------------------------------
// Image transport
//
// The three calls below sit outside `request` because the shape of the payload
// forbids it: raw bytes going up, raw bytes coming down. What they do NOT do is
// invent a second error convention - every failure is turned into the same
// ApiError the rest of the client throws, so ErrorBanner and Field render an
// upload failure exactly as they render a save failure.
// ---------------------------------------------------------------------------

/**
 * The release-note prefix under /admin, written once: kebab-case like every other
 * API path, unlike the site's own /releasenotes pages (releaseNoteUrls.ts in the
 * shared package says why the two spellings differ).
 */
const RELEASE_NOTES_API = '/release-notes';

/** The attachment library under /admin (S47), and one file in it. */
const ATTACHMENTS_API = '/attachments';

function attachmentPath(attachmentId: string): string {
  return `${ATTACHMENTS_API}/${encodeURIComponent(attachmentId)}`;
}

/**
 * The browser-facing path of one image.
 *
 * Carries the /api prefix in full, unlike the paths handed to `call`, because this
 * one is read by the browser (as a src, or by fetch) rather than by the request
 * helper that would otherwise add it.
 */
function adminArticleImagePath(articleId: string, fileName: string): string {
  return `/api/admin/articles/${encodeURIComponent(articleId)}/images/${encodeURIComponent(
    fileName,
  )}`;
}

/** The same, for an image attached to a release note. */
function adminReleaseNoteImagePath(releaseNoteId: string, fileName: string): string {
  return `/api/admin${RELEASE_NOTES_API}/${encodeURIComponent(
    releaseNoteId,
  )}/images/${encodeURIComponent(fileName)}`;
}

/** Mirrors the token handling in client.ts: a token failure is 401, not a network error. */
async function bearerToken(token: TokenProvider): Promise<string> {
  try {
    return await token();
  } catch (error) {
    throw new ApiError(
      401,
      'token-acquisition-failed',
      error instanceof Error
        ? `Could not get an access token: ${error.message}`
        : 'Could not get an access token.',
    );
  }
}

/** Reads ApiErrorBody out of a response that was not JSON-decoded by `request`. */
function toApiError(status: number, text: string): ApiError {
  let body: ApiErrorBody | null = null;
  try {
    body = text.length > 0 ? (JSON.parse(text) as ApiErrorBody) : null;
  } catch {
    body = null;
  }

  const error = body?.error;
  return new ApiError(
    status,
    error?.code ?? `http-${status}`,
    error?.message ?? `The request failed (${status}).`,
    error?.details ?? {},
    error?.conflict,
  );
}

/**
 * POSTs the RAW BYTES as the body, with the fields in the query string, reporting
 * progress.
 *
 *     POST /api/admin/articles/{id}/images?fileName=shot.png&replace=false
 *     Content-Type: image/png
 *     <the bytes>
 *
 * NOT multipart/form-data, and that is the server's contract rather than a
 * preference here: `acceptImageBytes` mounts `express.raw` on this one route and
 * `resolveDeclaredUploadType` rejects a `multipart/form-data` upload by name with a
 * 422. Sending a FormData here fails every single upload - which is exactly what
 * this function used to do. The body is the image, so the fields have nowhere to go
 * but the query string, and they arrive as text; `articleImageUploadSchema` coerces
 * `replace` from 'true' / 'false' for that reason.
 *
 * XMLHttpRequest rather than fetch for one reason: fetch has no upload progress
 * event. The brief says a silent failure on a 6 MB paste is the worst outcome, and
 * a silent success that takes forty seconds is the second worst.
 *
 * The query string and the declared Content-Type are both built by the shared
 * module - see `articleImageUploadQuery` and `declaredArticleImageUploadType`,
 * which carry the reasoning for each.
 *
 * Addressed by the collection path rather than an article id since S42: a
 * release note's images take exactly this wire format at their own path, and a
 * second copy of this function would be the second place the format lived.
 */
async function uploadImage(
  token: TokenProvider,
  imagesPath: string,
  file: Blob,
  options: ArticleImageUploadOptions,
): Promise<AdminArticleImageUploadResponse> {
  // The query string comes from the shared module, which is the counterpart of
  // articleImageUploadSchema on the server. Building it here would put the wire
  // format in two places again, which is the mistake this function is the repair
  // for.
  const query = articleImageUploadQuery(options);

  return postImageBytes<AdminArticleImageUploadResponse>(
    token,
    `${imagesPath}?${query}`,
    file,
    options,
  );
}

/**
 * Raw image bytes up, JSON down. Shared by the three image uploads - an article
 * image, a release-note image and the site icon - because the wire format and
 * every failure mode are identical between them; only the URL differs. The XHR
 * itself is sendBytes below, which the attachment uploads share too.
 *
 * The declared Content-Type comes from the shared module - see
 * `declaredArticleImageUploadType`, which carries the reasoning.
 */
async function postImageBytes<T>(
  token: TokenProvider,
  url: string,
  file: Blob,
  options: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<T> {
  return sendBytes<T>(token, url, file, {
    ...options,
    method: 'POST',
    contentType: declaredArticleImageUploadType(file.type),
    noun: 'image',
  });
}

/**
 * An attachment's bytes, up (S47): the image transport with the file's name in
 * the query string, POST for a new file and PUT for a replacement.
 *
 * ALWAYS application/octet-stream, whatever the file is. The server decides the
 * type from the name and checks it against the bytes, so a declared type tells
 * it nothing - and two declared types break the upload outright: a .json file
 * labelled application/json would be parsed by the JSON body parser in front of
 * every route and arrive as an object, and a form type is refused by name. The
 * browser's own label for a .pbix is whatever the operating system guessed, so
 * passing it on would make an upload's fate depend on the author's machine.
 */
function uploadAttachmentBytes(
  token: TokenProvider,
  method: 'POST' | 'PUT',
  url: string,
  file: Blob,
  options: AttachmentUploadOptions,
): Promise<AdminAttachmentUploadResponse> {
  const { fileName, ...rest } = options;
  return sendBytes<AdminAttachmentUploadResponse>(token, `${url}?${attachmentUploadQuery(fileName)}`, file, {
    ...rest,
    method,
    contentType: 'application/octet-stream',
    noun: 'file',
  });
}

/**
 * The one XHR in the client: raw bytes up, JSON down, with progress. The image
 * uploads and (S47) the attachment uploads differ in the method, the declared
 * type and the word the messages use - never in the failure modes, which is why
 * they share this.
 */
async function sendBytes<T>(
  token: TokenProvider,
  url: string,
  file: Blob,
  options: {
    method: 'POST' | 'PUT';
    contentType: string;
    noun: 'image' | 'file';
    onProgress?: (fraction: number) => void;
    signal?: AbortSignal;
  },
): Promise<T> {
  const bearer = await bearerToken(token);
  const { noun } = options;

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method, url);
    xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
    xhr.setRequestHeader('Accept', 'application/json');
    // Set explicitly rather than left to the Blob: XHR would send the blob's own
    // type verbatim, including a label the server refuses outright.
    xhr.setRequestHeader('Content-Type', options.contentType);

    const { onProgress, signal } = options;

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      });
    }

    const abort = (): void => xhr.abort();
    const detach = (): void => signal?.removeEventListener('abort', abort);

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('The upload was cancelled.', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', abort, { once: true });
    }

    xhr.addEventListener('load', () => {
      detach();

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          // The bytes are stored but we cannot say what the server recorded.
          // Saying so is the only honest option: inventing an answer would put
          // a reference in the markdown, or a hash in the header, that points at
          // nothing.
          reject(
            new ApiError(
              xhr.status,
              'malformed-response',
              `The ${noun} was uploaded but the answer could not be read. Reload the page to see what was stored.`,
            ),
          );
        }
        return;
      }

      reject(toApiError(xhr.status, xhr.responseText));
    });

    xhr.addEventListener('error', () => {
      detach();
      reject(
        new ApiError(
          0,
          'network-error',
          `The ${noun} could not be uploaded. Check your connection and try again.`,
        ),
      );
    });

    xhr.addEventListener('abort', () => {
      detach();
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
    });

    xhr.send(file);
  });
}

/**
 * GETs the stored bytes with the bearer token, for the editor preview.
 *
 * Takes the whole browser-facing path - one of the two builders above - rather
 * than an article id, so an article image and a release-note image share the one
 * fetch and its one error convention (S42, 2026-10-12).
 */
async function fetchImageBlob(
  token: TokenProvider,
  path: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const bearer = await bearerToken(token);

  let response: Response;
  try {
    const init: RequestInit = {
      headers: { Authorization: `Bearer ${bearer}`, Accept: 'image/*' },
    };
    if (signal) init.signal = signal;
    response = await fetch(path, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network-error', 'The image could not be loaded.');
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw toApiError(response.status, text);
  }

  return response.blob();
}
